// ============================================================
// FILE: src/pages/Insights.jsx  →  BLOG / INSIGHTS PAGE
// The articles grid. Article text & cover images are in
// backend/seed_data.py (BLOG ARTICLES).
// ============================================================

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { useSEO } from "../hooks/useSEO";
import { api } from "../lib/api";

function formatDate(d) {
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function Insights() {
  useSEO("Insights & Trade Intelligence — Evolve Trade & Logistics", "Export guides, import regulations, market updates and industry reports from the Evolve trade desk.");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.posts().then(setPosts).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <>
      <PageHero
        label="Insights"
        title="Trade intelligence,"
        accent="from the field"
        description="Export guides, regulation updates and market analysis written by the people who actually move the cargo."
        crumbs={[{ label: "Insights" }]}
      />

      <section className="pb-24 sm:pb-32" data-testid="insights-list">
        <div className="container-x">
          {loading ? (
            <div className="grid gap-6 lg:grid-cols-3">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-2xl bg-slate-900/5 dark:bg-white/5" />)}
            </div>
          ) : (
            <>
              {featured && (
                <Reveal>
                  <Link to={`/insights/${featured.slug}`} className="group block" data-testid={`post-featured-${featured.slug}`}>
                    <div className="relative overflow-hidden rounded-[2rem] border border-slate-900/10 dark:border-white/10">
                      <img src={featured.image} alt={featured.title} className="aspect-[21/9] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-8 sm:p-12">
                        <span className="glass rounded-full px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-teal-300">{featured.category}</span>
                        <h2 className="mt-4 max-w-3xl font-display text-2xl font-bold leading-tight text-white sm:text-4xl">{featured.title}</h2>
                        <p className="mt-3 flex items-center gap-4 text-xs text-slate-300">
                          <span>{formatDate(featured.date)}</span>
                          <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {featured.read_time}</span>
                        </p>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              )}
              <Stagger className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((p) => (
                  <StaggerItem key={p.slug}>
                    <Link to={`/insights/${p.slug}`} className="group block h-full" data-testid={`post-card-${p.slug}`}>
                      <article className="card-surface flex h-full flex-col !p-0 overflow-hidden">
                        <div className="aspect-[16/9] overflow-hidden">
                          <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </div>
                        <div className="flex flex-1 flex-col p-6">
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-teal-600 dark:text-teal-400">{p.category}</span>
                          <h3 className="mt-2.5 font-display text-lg font-semibold leading-snug tracking-tight">{p.title}</h3>
                          <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-foreground">{p.excerpt}</p>
                          <p className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
                            <span>{formatDate(p.date)} • {p.read_time}</span>
                            <ArrowRight className="h-4 w-4 text-teal-500 transition-transform duration-300 group-hover:translate-x-1" />
                          </p>
                        </div>
                      </article>
                    </Link>
                  </StaggerItem>
                ))}
              </Stagger>
            </>
          )}
        </div>
      </section>
    </>
  );
}
